import {
  Box,
  HStack,
  Input,
  Spinner,
  Text,
  VStack,
  type InputProps,
} from '@chakra-ui/react'
import { Globe2, MapPin, Store } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { LocationService } from '@/lib/services/locationService'
import type {
  CombinedLocationResult,
  CombinedLocationSearchResults,
  FreeTextLocationResult,
  StoreLocationResult,
} from '@/types/location'

interface LocationSearchInputProps {
  value: string
  onChange: (value: string) => void
  onSelect: (result: CombinedLocationResult) => void
  clientCode?: string
  placeholder?: string
  isDisabled?: boolean
  minLength?: number
  debounceMs?: number
  inputProps?: Partial<InputProps>
}

function hasResults(results: CombinedLocationSearchResults): boolean {
  return results.stores.length > 0 || results.locations.length > 0
}

function locationSubtitle(location: FreeTextLocationResult): string {
  return [location.municipality, location.province, location.country]
    .filter(Boolean)
    .join(', ')
}

function StoreRow({ store, onSelect }: { store: StoreLocationResult; onSelect: () => void }) {
  return (
    <Box
      px={3}
      py={2}
      cursor="pointer"
      _hover={{ bg: 'gray.100' }}
      onMouseDown={(event) => {
        event.preventDefault()
        onSelect()
      }}
    >
      <Text fontWeight="600" fontSize="sm">
        {store.store_name}
      </Text>
      <Text fontSize="xs" color="gray.600" noOfLines={1}>
        {[store.store_code, store.business_unit_name, store.address].filter(Boolean).join(' · ')}
      </Text>
    </Box>
  )
}

function LocationRow({ location, onSelect }: { location: FreeTextLocationResult; onSelect: () => void }) {
  const subtitle = locationSubtitle(location)

  return (
    <Box
      px={3}
      py={2}
      cursor="pointer"
      _hover={{ bg: 'gray.100' }}
      onMouseDown={(event) => {
        event.preventDefault()
        onSelect()
      }}
    >
      <Text fontWeight="600" fontSize="sm" noOfLines={1}>
        {location.display_name}
      </Text>
      {subtitle ? (
        <Text fontSize="xs" color="gray.600" noOfLines={1}>
          {subtitle}
        </Text>
      ) : null}
    </Box>
  )
}

function SectionHeader({
  icon,
  children,
}: {
  icon: ReactNode
  children: ReactNode
}) {
  return (
    <HStack px={3} py={1.5} bg="gray.50" borderBottom="1px solid" borderColor="gray.100" spacing={2}>
      {icon}
      <Text fontSize="11px" fontWeight="700" color="gray.600" textTransform="uppercase" letterSpacing="wide">
        {children}
      </Text>
    </HStack>
  )
}

export function LocationSearchInput({
  value,
  onChange,
  onSelect,
  clientCode,
  placeholder = 'Search store, address, landmark, city...',
  isDisabled,
  minLength = 3,
  debounceMs = 400,
  inputProps,
}: LocationSearchInputProps) {
  const [results, setResults] = useState<CombinedLocationSearchResults>({ stores: [], locations: [] })
  const [isSearching, setIsSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isFocused, setIsFocused] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    const term = value.trim()
    abortRef.current?.abort()

    if (term.length < minLength || isDisabled) {
      setResults({ stores: [], locations: [] })
      setIsSearching(false)
      setError(null)
      return
    }

    const controller = new AbortController()
    abortRef.current = controller
    setIsSearching(true)
    setError(null)

    const timeout = window.setTimeout(() => {
      LocationService.searchCombined(term, clientCode, controller.signal)
        .then((nextResults) => {
          if (controller.signal.aborted) return
          setResults(nextResults)
        })
        .catch(() => {
          if (controller.signal.aborted) return
          setResults({ stores: [], locations: [] })
          setError('Unable to search stores or locations.')
        })
        .finally(() => {
          if (!controller.signal.aborted) setIsSearching(false)
        })
    }, debounceMs)

    return () => {
      window.clearTimeout(timeout)
      controller.abort()
    }
  }, [clientCode, debounceMs, isDisabled, minLength, value])

  const showDropdown = isFocused && (isSearching || error || hasResults(results))

  return (
    <Box position="relative" zIndex={showDropdown ? 1400 : 'auto'}>
      <Input
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        isDisabled={isDisabled}
        autoComplete="off"
        {...inputProps}
      />
      {isSearching ? (
        <Box position="absolute" right={3} top="50%" transform="translateY(-50%)" zIndex={1}>
          <Spinner size="sm" />
        </Box>
      ) : null}
      {showDropdown ? (
        <Box
          position="absolute"
          zIndex={1401}
          w="100%"
          mt={1}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="lg"
          maxH="320px"
          overflowY="auto"
        >
          {isSearching ? (
            <HStack justify="center" py={3} spacing={2}>
              <Spinner size="sm" />
              <Text fontSize="sm" color="gray.600">
                Searching...
              </Text>
            </HStack>
          ) : error ? (
            <HStack py={3} px={3} spacing={2}>
              <MapPin size={14} color="#e53e3e" />
              <Text fontSize="sm" color="red.600">
                {error}
              </Text>
            </HStack>
          ) : (
            <VStack align="stretch" spacing={0}>
              {results.stores.length > 0 ? (
                <Box>
                  <SectionHeader icon={<Store size={13} />}>Stores</SectionHeader>
                  {results.stores.map((store) => (
                    <StoreRow
                      key={`${store.store_code ?? 'store'}-${store.id ?? store.store_name}`}
                      store={store}
                      onSelect={() => {
                        onSelect(store)
                        setResults({ stores: [], locations: [] })
                        setIsFocused(false)
                      }}
                    />
                  ))}
                </Box>
              ) : null}
              {results.locations.length > 0 ? (
                <Box>
                  <SectionHeader icon={<Globe2 size={13} />}>Locations</SectionHeader>
                  {results.locations.map((location) => (
                    <LocationRow
                      key={`${location.place_id ?? location.display_name}-${location.latitude}-${location.longitude}`}
                      location={location}
                      onSelect={() => {
                        onSelect(location)
                        setResults({ stores: [], locations: [] })
                        setIsFocused(false)
                      }}
                    />
                  ))}
                </Box>
              ) : null}
            </VStack>
          )}
        </Box>
      ) : null}
    </Box>
  )
}
